import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {UntypedFormBuilder, Validators} from '@angular/forms';
import {AlertService} from '../../sdk/service/alert.service';
import {PublicSubmitForm} from '../../sdk/interface/form/public-submit-form';
import {ROUTES} from '../../app.routes';
import {AuthenticationService} from '../../sdk/service/authentication.service';
import {finalize, Observable} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {EnvironmentService} from '../../sdk/service/environment.service';
import {ConfirmationService} from '../../sdk/service/confirmation.service';
import {environment} from '../../../environments/environment';
import {TEXT_ALIGNMENT} from '../../sdk/component/public-page-template/public-page-template.component';
import {Business, KycStatus, WebBusinessService} from 'web-angular-client';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent extends PublicSubmitForm implements OnInit
{
    loading = false;
    protected readonly TEXT_ALIGNMENT = TEXT_ALIGNMENT;
    private business: Business;

    constructor(
        protected router: Router,
        protected formBuilder: UntypedFormBuilder,
        protected alertService: AlertService,
        protected authenticationService: AuthenticationService,
        private ngxLoader: NgxUiLoaderService,
        protected environmentService: EnvironmentService,
        protected confirmationService: ConfirmationService,
        private webBusinessService: WebBusinessService)
    {
        super(router, formBuilder, alertService, authenticationService, environmentService);
    }

    isSignUpFeePaid(): boolean
    {
        return this.business?.signUpFeePaid;
    }

    isKycPending(): boolean
    {
        return this.business?.businessConfig?.kycStatus === KycStatus.PENDING;
    }

    loadBusiness(businessId: number)
    {
        this.webBusinessService.getBusinessFromId(businessId).subscribe({
            next: business =>
            {
                this.business = business;
                this.displayPopUp();
            }
        });
    }

    ngOnInit()
    {
        this.form = this.formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
            password: ['', [Validators.required]]
        });
    }

    submit()
    {
        this.ngxLoader.start();
        this.authenticate().pipe(finalize(() => this.ngxLoader.stop())).subscribe({
            next: () =>
            {
                this.loading = false;
                this.debugOutput('Logged in successfully routing to ' + ROUTES.SECURE.HOME.VIEW.path);
                this.loadBusiness(this.authenticationService.getUserLogin().businessId);
                this.router.navigate([ROUTES.SECURE.HOME.VIEW.path]).then();
            }, error: (error: HttpErrorResponse) =>
            {
                this.loading = false;
                this.debugOutput('Login failed', error);

                if (error.status === HttpStatusCode.TooManyRequests && error.error?.remainingSeconds)
                {
                    this.alertService.lockout(error.error.errorMessage, error.error.remainingSeconds);
                }
                else if (error.status === HttpStatusCode.UnprocessableEntity)
                {
                    this.alertService.info(error.error.errorMessage);
                }
                else
                {
                    this.alertService.error('Login failed. Please try again later');
                }
            }
        });
    }

    private displayPopUp()
    {
        let popupMessage = '';
        let okButtonText = 'OK';

        if (this.isKycPending() && !this.isSignUpFeePaid())
        {
            popupMessage =
                'Your access is currently limited as your KYC verification is in progress and your sign-up fee is still outstanding. Please settle your sign-up fee and check back later to confirm the completion of your KYC process.';
        }
        else if (this.isKycPending() && this.isSignUpFeePaid())
        {
            popupMessage =
                'Your access is currently limited while your KYC verification is being processed. This is an automated process, so please check back later to confirm its completion.';
        }
        else if (!this.isKycPending() && !this.isSignUpFeePaid())
        {
            popupMessage =
                'Your access is currently limited because your sign-up fee remains unpaid. Please complete your payment to unlock full access.';
        }
        else
        {
            return; // No need to show a popup if none of the conditions match
        }

        const hasWalkthrough = environment.instanceName === 'GetPaid';
        if (hasWalkthrough)
        {
            popupMessage += ' While you wait, why don’t you take a tour of the GetPaid features.';
            okButtonText = 'Take a Tour While I Wait';
        }

        this.showConfirmationPopup(popupMessage, okButtonText, hasWalkthrough);
    }

    private showConfirmationPopup(popupMessage: string, okButtonText: string, hasWalkthrough: boolean)
    {
        this.confirmationService
            .confirm('INFO', popupMessage, okButtonText, '', 'md', true)
            .then((takeTour: boolean) =>
            {
                if (takeTour && hasWalkthrough)
                {
                    this.router.navigate([ROUTES.SECURE.WALKTHROUGH.path]).then();
                }
                else
                {
                    // TO-DO save that user opted to skip
                }
            });
    }

    /**
     * Authenticates and performs a 'login' for the user. The following steps are performed:
     * <ol>
     *      <li>Ping the web server to check if it is ready to login a user.</li>
     *      <li>Perform a 'logout' which will invalidate any user sessions that haven't expired or been invalidated
     * yet.</li>
     *      <li>Perform a login, using basic auth to verify the user's credentials.</li>
     * </ol>
     */
    private authenticate(): Observable<any>
    {
        // First attempt to see if the server is online before attempting login
        let authenticateObservable: Observable<any> = this.authenticationService.ping()
            .pipe(switchMap(() => this.authenticationService.logout()));

        authenticateObservable = authenticateObservable.pipe(switchMap(
            () => this.authenticationService.login(this.f.email.value, this.f.password.value)));

        return authenticateObservable;
    }
}